/** Building blocks of a record page: header, facts, the gap list, links. */
import { Link, Navigate } from 'react-router'
import { Clock, ExternalLink, History, Pencil, Plus } from 'lucide-react'
import { Cover } from './Cover'
import { MergeButton, ReportButton } from './RecordActions'
import { OwnedButton } from './Collection'
import { fieldLabel, plural, recordHref } from '../lib/format'
import { signInHref, useSession } from '../lib/session'
import {
  attributedFields,
  type Attribution,
  type EntityType,
  type ExternalId,
  type Link as LinkT,
} from '../../shared/model'

export interface Crumb {
  label: string
  to: string
}

interface HeaderProps {
  type: EntityType
  id: string
  /** Where this record sits: "Systems / Dungeons & Dragons". The record itself is the h1. */
  crumbs: Crumb[]
  title: string
  subtitle?: React.ReactNode
  cover?: string | null
  score: number
  /** Covers exist for systems and books only. */
  hasCover?: boolean
  /** A book whose cover is withheld on purpose. */
  coverWithheld?: boolean
  /** A one-page RPG system: a missing cover is a title card, not a gap. */
  onePage?: boolean
  /** Shown in the cover's place when there is no cover: a creator's portrait. */
  visual?: React.ReactNode
  editHref: string
  historyHref: string
  pending: number
  pendingHref: string
  deleted?: boolean
  /** Extra secondary actions (claiming). Report and merge are always offered. */
  actions?: React.ReactNode
  children?: React.ReactNode
}

/** Title block with the cover, primary action, and the pending-edit notice. */
export function RecordHeader(p: HeaderProps) {
  const session = useSession()
  const editTo = session.user ? p.editHref : signInHref(p.editHref)
  return (
    <header
      className={`record-head${p.hasCover === false && !p.visual ? ' no-cover' : ''}${p.visual ? ' has-visual' : ''}`}
    >
      {p.visual ??
        (p.hasCover !== false && (
          <Cover
            cover={p.cover}
            title={p.title}
            score={p.score}
            size="lg"
            showScore
            eager
            withheld={p.coverWithheld}
            onePage={p.onePage}
          />
        ))}
      <div className="record-head-body">
        <nav aria-label="Breadcrumb" className="crumbs">
          <ol>
            {p.crumbs.map((c) => (
              <li key={c.to}>
                <Link to={c.to}>{c.label}</Link>
              </li>
            ))}
          </ol>
        </nav>
        <h1 className="record-title">{p.title}</h1>
        {p.subtitle && <div className="record-subtitle">{p.subtitle}</div>}
        {p.deleted && <p className="notice notice-danger">This record has been deleted. Its history is kept.</p>}
        {p.pending > 0 && (
          <Link to={p.pendingHref} className="notice notice-pending">
            <Clock size={16} aria-hidden="true" />
            {plural(p.pending, 'edit')} to this record {p.pending === 1 ? 'is' : 'are'} waiting for review
          </Link>
        )}
        {p.children}
      </div>
      {!p.deleted && (
        <div className="record-actions">
          <Link to={editTo} className="button button-primary">
            <Pencil size={16} aria-hidden="true" /> Edit
          </Link>
          <Link to={p.historyHref} className="button button-quiet">
            <History size={16} aria-hidden="true" /> History
          </Link>
          {(p.type === 'book' || p.type === 'system') && <OwnedButton type={p.type} id={p.id} />}
          <div className="record-more">
            {p.actions}
            <ReportButton targetType={p.type} targetId={p.id} />
            <MergeButton type={p.type} id={p.id} name={p.title} />
          </div>
        </div>
      )}
    </header>
  )
}

/**
 * The completeness gauge carried onto the record page: every empty field a complete record
 * should have, each a direct link to that field in the edit form.
 */
export function GapList({ missing, score, editHref }: { missing: string[]; score: number; editHref: string }) {
  const session = useSession()
  if (!missing.length) {
    return (
      <section className="gaps is-complete" aria-label="Completeness">
        <p>
          <strong>Complete.</strong> Every field this record should have is filled in. Corrections are still welcome.
        </p>
      </section>
    )
  }
  const href = (field: string) => {
    const target = `${editHref}#field-${field}`
    return session.user ? target : signInHref(target)
  }
  return (
    <section className="gaps" aria-label="Missing information">
      <p className="gaps-lede">
        <strong>{score}% complete.</strong> Know any of these? Add {missing.length === 1 ? 'it' : 'them'}:
      </p>
      <ul className="gaps-list">
        {missing.map((f) => (
          <li key={f}>
            <Link to={href(f === 'identifier' ? 'isbn' : f)} className="gap">
              <Plus size={14} aria-hidden="true" />
              {fieldLabel(f)}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}

export interface Fact {
  label: string
  value: React.ReactNode
}

/** A definition list of the facts that are known. Empty values are left to the gap list. */
export function Facts({ facts }: { facts: Fact[] }) {
  const known = facts.filter((f) => f.value !== '' && f.value !== null && f.value !== undefined && f.value !== false)
  if (!known.length) return null
  return (
    <dl className="facts">
      {known.map((f) => (
        <div key={f.label} className="fact">
          <dt>{f.label}</dt>
          <dd>{f.value}</dd>
        </div>
      ))}
    </dl>
  )
}

export function LinkList({ links, title }: { links: LinkT[]; title: string }) {
  if (!links.length) return null
  return (
    <section className="section">
      <h2 className="section-title">{title}</h2>
      <ul className="link-list">
        {links.map((l) => (
          <li key={l.url}>
            <a href={l.url} target="_blank" rel="noopener noreferrer nofollow ugc">
              <span>{l.label || new URL(l.url).hostname.replace(/^www\./, '')}</span>
              <ExternalLink size={14} aria-hidden="true" />
            </a>
            <span className="link-host">{new URL(l.url).hostname.replace(/^www\./, '')}</span>
          </li>
        ))}
      </ul>
    </section>
  )
}

/** Long text with paragraph breaks preserved. Plain text only; nothing is rendered as HTML. */
export function Prose({ text }: { text: string }) {
  if (!text) return null
  return (
    <div className="prose">
      {text.split(/\n{2,}/).map((para, i) => (
        <p key={i}>{para}</p>
      ))}
    </div>
  )
}

const ATTRIBUTED_TEXT: Record<string, string> = { description: 'description', tags: 'tags' }

/**
 * Credit for the fields a record took from another catalogue. Those fields are not CC0, so the
 * page says whose they are and that reusing them needs that credit.
 */
export function Attributions({ attributions }: { attributions?: Attribution[] }) {
  const by = attributedFields({ attributions })
  if (!by.size) return null
  const sources = [...new Set(by.values())]
  return (
    <div className="attributions">
      {sources.map((a) => {
        const fields = [...by].filter(([, s]) => s === a).map(([f]) => ATTRIBUTED_TEXT[f] ?? f)
        // "The tags are", like "the description and tags are": tags are plural on their own.
        const many = fields.length > 1 || fields[0] === 'tags'
        const what = `The ${fields.join(' and ')} ${many ? 'are' : 'is'}`
        return (
          <p key={a.url} className="attribution">
            {what} from{' '}
            <a href={a.url} target="_blank" rel="noopener noreferrer">
              {a.source}
            </a>{' '}
            and not CC0: credit {a.source} if you reuse {many ? 'them' : 'it'}.
          </p>
        )
      })}
    </div>
  )
}

/**
 * The stepped category rail: one tab per category, its width proportional to how many books it
 * holds, so the shape of a system's shelf reads at a glance.
 */
export function CategoryRail({
  categories,
  active,
  onSelect,
  label,
}: {
  categories: Array<{ category: string; count: number }>
  active: string
  onSelect: (c: string) => void
  label: (c: string) => string
}) {
  const total = categories.reduce((n, c) => n + c.count, 0)
  if (categories.length < 2) return null
  return (
    <div className="rail" role="tablist" aria-label="Filter by category">
      <button
        type="button"
        role="tab"
        aria-selected={active === ''}
        className="rail-tab rail-all"
        onClick={() => onSelect('')}
      >
        <span className="rail-label">All</span>
        <span className="rail-count">{total}</span>
      </button>
      {categories.map((c) => (
        <button
          key={c.category}
          type="button"
          role="tab"
          aria-selected={active === c.category}
          className="rail-tab"
          style={{ '--share': total ? c.count / total : 0 } as React.CSSProperties}
          onClick={() => onSelect(c.category)}
          title={`${label(c.category)}: ${c.count}`}
        >
          <span className="rail-label">{label(c.category)}</span>
          <span className="rail-count">{c.count}</span>
        </button>
      ))}
    </div>
  )
}

/** A record merged into another lives on as a redirect, so old links keep working. */
export function MergedRedirect({ type, to }: { type: EntityType; to: string | null | undefined }) {
  return to ? <Navigate to={recordHref(type, to)} replace /> : null
}

/** Typed links to other records, from this record and to it ("Requires", "Required by"). */
export function RelationList({
  type,
  outgoing,
  incoming,
  labels,
  inverse,
}: {
  type: EntityType
  outgoing: Array<{ type: string; id: string; name: string }>
  incoming: Array<{ type: string; id: string; name: string }>
  labels: Record<string, string>
  inverse: Record<string, string>
}) {
  const rows = [
    ...outgoing.map((r) => ({ label: labels[r.type] ?? r.type, id: r.id, name: r.name })),
    ...incoming.map((r) => ({ label: inverse[r.type] ?? r.type, id: r.id, name: r.name })),
  ]
  if (!rows.length) return null
  const grouped = new Map<string, Array<{ id: string; name: string }>>()
  for (const r of rows) grouped.set(r.label, [...(grouped.get(r.label) ?? []), { id: r.id, name: r.name }])
  return (
    <section className="section">
      <h2 className="section-title">Related</h2>
      <dl className="facts">
        {[...grouped].map(([lbl, items]) => (
          <div key={lbl} className="fact">
            <dt>{lbl}</dt>
            <dd>
              <ul className="inline-list">
                {items.map((i) => (
                  <li key={i.id}>
                    <Link to={recordHref(type, i.id)}>{i.name}</Link>
                  </li>
                ))}
              </ul>
            </dd>
          </div>
        ))}
      </dl>
    </section>
  )
}

/** Identifiers in other catalogues, linked where the source has a known URL pattern. */
const EXTERNAL_URLS: Record<string, (id: string) => string> = {
  drivethrurpg: (id) => `https://www.drivethrurpg.com/en/product/${encodeURIComponent(id)}`,
  dmsguild: (id) => `https://www.dmsguild.com/en/product/${encodeURIComponent(id)}`,
  rpggeek: (id) => `https://rpggeek.com/rpgitem/${encodeURIComponent(id)}`,
}

export function ExternalIds({ ids }: { ids: ExternalId[] }) {
  if (!ids.length) return null
  return (
    <ul className="inline-list">
      {ids.map((e) => {
        const url = EXTERNAL_URLS[e.source]?.(e.id)
        return (
          <li key={`${e.source}:${e.id}`}>
            <span className="mono" translate="no">
              {url ? (
                <a href={url} target="_blank" rel="noopener noreferrer nofollow">
                  {e.source} {e.id}
                </a>
              ) : (
                `${e.source} ${e.id}`
              )}
            </span>
          </li>
        )
      })}
    </ul>
  )
}
